import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  DynamicFeed as BulkIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  attachSparte,
  detachSparte,
  fetchUnassignedSparten,
  updateSparte,
  updateSparten,
  fetchClaimCauses,
} from "@accredo/fe-shared/services/claimFormService.js";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import ThemedButton from "@accredo/fe-shared/theme/components/Button.jsx";
import { useConfirm } from "@accredo/fe-shared/contexts/ConfirmContext.jsx";
import ClaimFormSparteEditDialog from "./ClaimFormSparteEditDialog.jsx";
import ClaimFormSparteBulkDialog from "./ClaimFormSparteBulkDialog.jsx";

export default function ClaimFormSpartenTab({ claimForm, onReload }) {
  const { enqueueSnackbar } = useSnackbar();
  const [availableSparten, setAvailableSparten] = useState([]);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(true);
  const [selectedSparte, setSelectedSparte] = useState(null);
  const [selectedCoverageType, setSelectedCoverageType] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [claimCauses, setClaimCauses] = useState([]);
  const [editingSparte, setEditingSparte] = useState(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const confirm = useConfirm();

  const coverageTypes = claimForm.current_definition?.coverage_types ?? [];
  const hasCoverageTypes = coverageTypes.length > 0;

  const loadAvailable = useCallback(() => {
    setIsLoadingAvailable(true);
    fetchUnassignedSparten()
      .then((res) => setAvailableSparten(res?.data ?? []))
      .catch(() =>
        enqueueSnackbar("Verfügbare Sparten konnten nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setIsLoadingAvailable(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadAvailable();
  }, [loadAvailable]);

  useEffect(() => {
    fetchClaimCauses()
      .then((causes) => setClaimCauses(Array.isArray(causes) ? causes : []))
      .catch(() =>
        enqueueSnackbar("Ursachen konnten nicht geladen werden", {
          variant: "error",
        })
      );
  }, [enqueueSnackbar]);

  const handleAdd = async () => {
    if (!selectedSparte) {
      return;
    }
    setIsSaving(true);
    try {
      await attachSparte(claimForm.id, selectedSparte.id, selectedCoverageType);
      setSelectedSparte(null);
      setSelectedCoverageType("");
      await onReload();
      loadAvailable();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ??
          "Sparte konnte nicht hinzugefügt werden",
        {
          variant: "error",
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (changes) => {
    setIsSaving(true);
    try {
      await updateSparte(claimForm.id, editingSparte.id, changes);
      setEditingSparte(null);
      await onReload();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ??
          "Sparte konnte nicht gespeichert werden",
        { variant: "error" }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkSave = async (sparte_ids, changes) => {
    setIsSaving(true);
    try {
      await updateSparten(claimForm.id, sparte_ids, changes);
      setIsBulkOpen(false);
      await onReload();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ??
          "Sparten konnten nicht gespeichert werden",
        { variant: "error" }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (sparte) => {
    const confirmed = await confirm({
      title: "Sparte entfernen",
      message: `Sparte „${sparte.name}" wirklich vom Formular entfernen?`,
      confirmLabel: "Entfernen",
      destructive: true,
    });
    if (!confirmed) {
      return;
    }
    setIsSaving(true);
    try {
      await detachSparte(claimForm.id, sparte.id);
      await onReload();
      loadAvailable();
    } catch (error) {
      enqueueSnackbar("Sparte konnte nicht entfernt werden", {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: "name",
          header: "Sparte",
        },
        hasCoverageTypes && {
          id: "coverage_type",
          header: "Deckungsart",
          accessorFn: (sparte) =>
            coverageTypes.find(
              (coverageType) => coverageType.key === sparte.coverage_type
            )?.label,
          Cell: ({ cell }) =>
            cell.getValue() ?? (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                color="warning.main"
              >
                <WarningIcon fontSize="small" />
                fehlt
              </Box>
            ),
        },
        {
          id: "cost_types",
          header: "Kostenarten",
          enableSorting: false,
          accessorFn: (sparte) =>
            sparte.cost_types ? `${sparte.cost_types.length} eigene` : "Standard",
        },
        {
          id: "claim_causes",
          header: "Ursachen",
          enableSorting: false,
          Cell: ({ row: { original: sparte } }) =>
            sparte.claim_causes
              ? `${claimCauses.filter((cause) => sparte.claim_causes.includes(cause.cause_id)).length} von ${claimCauses.length}`
              : "Alle",
        },
        {
          id: "actions",
          header: "",
          size: 56,
          enableSorting: false,
          enableColumnHiding: false,
          muiTableBodyCellProps: { align: "right" },
          Cell: ({ row }) => (
            <Tooltip title="Sparte entfernen">
              <span>
                <IconButton
                  size="small"
                  aria-label={`${row.original.name} entfernen`}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemove(row.original);
                  }}
                  disabled={isSaving}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ),
        },
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coverageTypes, hasCoverageTypes, claimCauses, isSaving]
  );

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Zugeordnete Sparten
          </Typography>
          {claimForm.sparten?.length > 1 && (
            <ThemedButton
              variant="primary"
              size="small"
              startIcon={<BulkIcon />}
              onClick={() => setIsBulkOpen(true)}
              disabled={isSaving}
            >
              Sammelbearbeitung
            </ThemedButton>
          )}
        </Box>
        <DataTable
          columns={columns}
          data={claimForm.sparten ?? []}
          manualSorting={false}
          manualFiltering={false}
          enableTopToolbar={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          onRowClick={(sparte) => !isSaving && setEditingSparte(sparte)}
          testId="claim-form-sparten-table"
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Sparte hinzufügen
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Autocomplete
            value={selectedSparte}
            onChange={(_event, value) => setSelectedSparte(value)}
            options={availableSparten}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoadingAvailable}
            noOptionsText="Keine unzugeordneten Sparten verfügbar"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sparte"
                size="small"
                placeholder="Sparte suchen…"
              />
            )}
            sx={{ minWidth: 350 }}
          />
          {hasCoverageTypes && (
            <TextField
              select
              size="small"
              label="Deckungsart"
              value={selectedCoverageType}
              onChange={(event) => setSelectedCoverageType(event.target.value)}
              disabled={isSaving}
              slotProps={{
                select: { native: true },
                htmlInput: { "aria-label": "Deckungsart" },
              }}
              sx={{ minWidth: 200 }}
            >
              <option value="" disabled />
              {coverageTypes.map((coverageType) => (
                <option key={coverageType.key} value={coverageType.key}>
                  {coverageType.label}
                </option>
              ))}
            </TextField>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={
              !selectedSparte ||
              (hasCoverageTypes && !selectedCoverageType) ||
              isSaving
            }
          >
            Hinzufügen
          </Button>
        </Box>
      </Box>
      {isBulkOpen && (
        <ClaimFormSparteBulkDialog
          sparten={claimForm.sparten}
          coverageTypes={coverageTypes}
          definition={claimForm.current_definition?.definition}
          causes={claimCauses}
          isSaving={isSaving}
          onSave={handleBulkSave}
          onClose={() => setIsBulkOpen(false)}
        />
      )}
      {editingSparte && (
        <ClaimFormSparteEditDialog
          sparte={editingSparte}
          coverageTypes={coverageTypes}
          definition={claimForm.current_definition?.definition}
          causes={claimCauses}
          isSaving={isSaving}
          onSave={handleSave}
          onClose={() => setEditingSparte(null)}
        />
      )}
    </Box>
  );
}
